import {Box, BoxProps, Flex, Heading, Link} from '@chakra-ui/react'
import {useTranslation} from 'next-i18next'
import {FC, useMemo} from 'react'
import {parseCookies} from 'nookies'
import styled from '@emotion/styled'
import Button from '_app/components/Buttons/Button'
import PriceText from '_app/components/PriceText'
import {
  BundleProduct,
  CartItemInput,
  CurrencyEnum,
  GetCartDocument,
  InputMaybe,
  PriceRange,
  ProductInterface,
} from '_app/graphql/generated/graphql'
import {useAddProductsToCartMutation} from '_app/graphql/mutations/cart/AddProductToCart'
import {useGetCartQuery} from '_app/graphql/queries/cart/GetCartDocumentSmall'
import {APP_ROUTES} from '_app/routes'
import ProductNotification from '_app/../modules/product/components/ProductNotification'
import RequestHelper from '../../../helpers/RequestHelper/RequestHelper'
import {cart} from '../../../_app/cache'
import ImageHelper from '../../../helpers/ImageHelper/ImageHelper'
import ArrayHelper from '../../../helpers/ArrayHelper/ArrayHelper'
import {useRouter} from '../../../_app/hooks/useRouter'

interface CartCrosssellProps extends BoxProps {
  bgColorImg?: string;
  price_range?: PriceRange;
  setData?: any;
}

const SmallLink = styled(Link)`
  &::after {
    width: 0;
    height: 0;
  }
`

const CartCrosssell: FC<CartCrosssellProps> = ({
  width = '100%',
  bgColor = '#fff',
  border = 'solid 1px #ede7e1',
  bgColorImg = 'main',
  p = {
    base: 4,
    lg: 6,
  },
  setData,
  ...props
}) => {
  const { t } = useTranslation('cart')
  const { locale } = useRouter()
  const { cartId } = parseCookies()
  const { data } = useGetCartQuery({
    variables: {
      cartId: cartId,
    },
  })

  const [addProductsToCart] = useAddProductsToCartMutation()

  const crossSells = useMemo(() => {
    const allCrossSells = (data?.cart?.items || []).flatMap(
      item => item?.product.crosssell_products
    )

    const availableCrossSells = allCrossSells.filter(
      (product): product is ProductInterface => !!product?.salable_qty
    )

    const uniqueCrossSells = ArrayHelper.removeDuplicates(availableCrossSells, 'sku')

    return uniqueCrossSells.sort(() => Math.random() - 0.5).slice(0, 2)
  }, [data])

  const addCrosssellToCartHandler = (productName: string, idx: number) => {
    const currentCrossell = crossSells[idx] as BundleProduct

    if (productName !== currentCrossell?.name) {
      return
    }
    if (!currentCrossell?.sku) {
      throw new Error(`Invalid sku: "${currentCrossell?.sku}"`)
    }

    const addProductToCartVariables: {
      cartId: string;
      cartItems: CartItemInput[];
    } = {
      cartId: cartId,
      cartItems: [{ quantity: 1, sku: currentCrossell.sku ?? '' }],
    }

    const bundleOptionUids:
      | InputMaybe<InputMaybe<string | undefined>[]>
      | undefined = (currentCrossell as BundleProduct)?.items?.[idx]?.options
      ?.filter(item => item?.uid)
      ?.map(item => item?.uid)

    if (!!bundleOptionUids?.length) {
      addProductToCartVariables.cartItems[idx].selected_options =
        bundleOptionUids as InputMaybe<InputMaybe<string>[]> | undefined
    }

    addProductsToCart({
      variables: addProductToCartVariables,
      refetchQueries: [GetCartDocument],
      onCompleted: (res: any) => {
        const response = RequestHelper.onComplete(res, 'addProductsToCart', locale)
        if (response) {
          cart(response)
          if (setData) {
            setData(response)
          }
        }
      }
    }).catch((error) => RequestHelper.internalServerError(error))
  }

  return (
    <Box {...props}>
      {crossSells.map((crosssell, idx) => {
        const bgImageSize = 94
        const bgImage = crosssell?.image?.url
          ? ImageHelper.cloudflareLoader({
              src: crosssell.image.url,
              width: bgImageSize,
              quality: 75,
            })
          : ''

        return (
          <Box
            key={crosssell?.id || idx}
            width={width}
            bgColor={bgColor}
            border={border}
            p={p}
            my={{ base: 4, lg: 6 }}
          >
            <Flex>
              <Box
                mr={{
                  base: 3,
                  lg: 6,
                }}
              >
                <Box
                  w={`${bgImageSize}px`}
                  h={`${bgImageSize}px`}
                  bgImage={bgImage}
                  bgColor={bgColorImg}
                  bgSize="cover"
                  bgRepeat="no-repeat"
                  bgPosition="center"
                />
              </Box>
              <Flex
                flexWrap={{
                  base: 'wrap',
                  lg: 'nowrap',
                }}
                flexDirection={{
                  base: 'column',
                  lg: 'row',
                }}
                justifyContent={{
                  base: 'space-between',
                  lg: 'space-between',
                }}
                w="100%"
              >
                <Box
                  width={{
                    base: '100%',
                    lg: '60%',
                  }}
                  mb={{
                    base: 4,
                    lg: 0,
                  }}
                >
                  <SmallLink
                    href={`/${locale}${APP_ROUTES.PRODUCTS.BY_URL_KEY(crosssell?.url_key || '#')}.html`}
                  >
                    <Heading
                      maxWidth="20rem"
                      noOfLines={{
                        base: 2,
                        lg: 0,
                      }}
                      as="h3"
                      fontSize={16}
                      lineHeight={1.375}
                      fontWeight="light"
                    >
                      {crosssell?.name || ''}
                    </Heading>
                  </SmallLink>
                </Box>

                <Flex
                  flexDir={{
                    base: 'row',
                    md: 'column',
                  }}
                  gap={{
                    base: 2,
                    md: 0,
                  }}
                  alignItems={{
                    lg: 'flex-end',
                  }}
                  minW="4.5rem"
                  fontWeight={600}
                >
                  <PriceText
                    fontWeight={400}
                    amount={{
                      currency:
                        crosssell?.price_range?.minimum_price?.regular_price
                          ?.currency || CurrencyEnum.Pln,
                      value:
                        crosssell?.price_range?.minimum_price?.regular_price
                          .value ||
                        crosssell?.price_range?.minimum_price?.regular_price
                          ?.value,
                    }}
                    floating
                    minimumFractionDigits={2}
                  />
                </Flex>
              </Flex>
            </Flex>
            {!!crosssell?.salable_qty ? (
              <>
                <Button
                  mt={4}
                  width="full"
                  type="submit"
                  variant="thin"
                  onClick={() =>
                    crosssell?.name &&
                    addCrosssellToCartHandler(crosssell?.name, idx)
                  }
                >
                  {t('ADD_TO_CART', { ns: 'common' })}
                </Button>
              </>
            ) : (
              <Box pt={2}>
                <ProductNotification productId={`${crosssell?.id}`} />
              </Box>
            )}
          </Box>
        )
      })}
    </Box>
  )
}

export default CartCrosssell
